import { Object_Template } from "graphql/generated/graphql";

/**
 * Lifecycle-hook types that can have their input schema stamped. Mirrors the
 * backend `STAMPABLE_HOOK_TYPES` (resources/object_template.py) — `before_create`
 * is deliberately excluded: it is an object-template-level hook with a different
 * contract.
 */
export type HookType = "on_create" | "on_update" | "on_delete";

/** Response of POST /object_template/{id}/stamp_hook_input_schema. */
export interface StampResult {
    object_template_id: string;
    object_template_name: string;
    hook_type: HookType;
    flow_id: string;
    flow_name: string;
    current_input_schema: Record<string, unknown> | null;
    computed_input_schema: Record<string, unknown> | null;
    changed: boolean;
    applied: boolean;
}

export interface HookStampRow {
    type: HookType;
    label: string;
    /** The flow bound to this hook, or null when no flow is set. */
    flow: { id: string; name: string } | null;
}

interface HookDef {
    type: HookType;
    label: string;
    flowKey: "on_create_flow" | "on_update_flow" | "on_delete_flow";
}

const HOOK_DEFS: HookDef[] = [
    { type: "on_create", label: "On create", flowKey: "on_create_flow" },
    { type: "on_update", label: "On update", flowKey: "on_update_flow" },
    { type: "on_delete", label: "On delete", flowKey: "on_delete_flow" },
];

/**
 * Derive the per-hook rows the stamp panel renders from an object-template
 * record. Pure so it can be unit-tested without rendering. Each row resolves the
 * bound hook flow (falling back to the id when the name is missing) or null when
 * no flow is set for that hook.
 */
export const getHookStampRows = (
    resourceData?: Partial<Object_Template> | null
): HookStampRow[] =>
    HOOK_DEFS.map(({ type, label, flowKey }) => {
        const flow = resourceData?.[flowKey];
        return {
            type,
            label,
            flow: flow?.id ? { id: flow.id, name: flow.name ?? flow.id } : null,
        };
    });

/** True when at least one hook has a flow bound (so the panel is worth showing). */
export const hasStampableHook = (rows: HookStampRow[]): boolean =>
    rows.some((row) => row.flow !== null);

/**
 * The fields of an object-template attribute that influence the computed hook
 * `input_schema`. Any change to one of these makes a previously-stamped hook
 * schema potentially out of date.
 */
const projectSchemaAffectingAttribute = (attr: Record<string, unknown>) => ({
    id: attr?.id ?? null,
    name: attr?.name ?? null,
    datatype: attr?.datatype ?? null,
    is_nullable: attr?.is_nullable ?? null,
    allowed_values: attr?.allowed_values ?? null,
    default_value: attr?.default_value ?? null,
    reference_object_template_id: attr?.reference_object_template_id ?? null,
    reference_record_type: attr?.reference_record_type ?? null,
    sort_order: attr?.sort_order ?? null,
});

/**
 * Stable, deterministic fingerprint of an object template's attribute set,
 * restricted to the fields that affect the computed hook input schema (sorted by
 * sort_order, then id). When this string changes between renders, the template's
 * attributes changed in a way that may make a stamped hook input schema stale, so
 * the panel re-checks and, if needed, prompts the author to re-stamp. Pure so it
 * can be unit-tested and compared with ===.
 */
export const attributesFingerprint = (
    resourceData?: Partial<Object_Template> | null
): string => {
    const attrs =
        (resourceData?.object_template_attribute_list_object_template_id
            ?.object_template_attribute_list as
            | Array<Record<string, unknown>>
            | null
            | undefined) ?? [];
    const projected = attrs
        .filter(Boolean)
        .map(projectSchemaAffectingAttribute)
        .sort((left, right) => {
            const bySortOrder = Number(left.sort_order ?? 0) - Number(right.sort_order ?? 0);
            return bySortOrder !== 0
                ? bySortOrder
                : String(left.id).localeCompare(String(right.id));
        });
    return JSON.stringify(projected);
};

/**
 * Heuristic: does this input schema look like one produced by the "stamp hook
 * input schema" feature? A stamped hook schema always carries the engine's
 * hallmark top-level keys (`custom_object_id`, `provisioning_type`, and one of
 * `value` / `old_value`). We only auto-prompt to refresh an out-of-date schema
 * when it looks stamped, so authors who hand-wrote their own hook input schema
 * (and never used the feature) are never nagged on an attribute change.
 */
export const looksLikeStampedSchema = (
    schema: Record<string, unknown> | null | undefined
): boolean => {
    if (!schema || typeof schema !== "object") {
        return false;
    }
    const properties = (schema as { properties?: unknown }).properties;
    if (!properties || typeof properties !== "object") {
        return false;
    }
    const keys = Object.keys(properties as Record<string, unknown>);
    return (
        keys.includes("provisioning_type") &&
        keys.includes("custom_object_id") &&
        (keys.includes("value") || keys.includes("old_value"))
    );
};
